import classNames from "components/ui/classNames";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2Icon } from "lucide-react";
import { useRef } from "react";
import { FormattedMessage } from "react-intl";
import { useHotkeys } from "react-hotkeys-hook";

interface IndicatorSearchInputProps {
  resultsCount: number;
  loading?: boolean;
  placeholder?: string;
  noResultsText?: string;
  onSearch: (searchQuery: string) => void;
}
export default function IndicatorSearchInput({
  resultsCount,
  loading = false,
  placeholder,
  noResultsText = "Nada...",
  onSearch,
}: IndicatorSearchInputProps) {
  const searchQueryRef = useRef<HTMLInputElement | null>(null);
  useHotkeys(
    "ctrl+f, meta+f, f3",
    (event) => {
      event.preventDefault();
      searchQueryRef.current?.focus();
    },
    {
      preventDefault: true,
      ignoreModifiers: false,
    },
  );

  return (
    <div className="relative flex items-center">
      <input
        ref={searchQueryRef}
        type="text"
        maxLength={34}
        onChange={(event) => onSearch(event.target.value)}
        spellCheck={false}
        autoComplete="off"
        placeholder={placeholder}
        className={classNames(
          "block w-full rounded-md border-0 px-3 py-3 pr-14 text-gray-900 shadow-sm",
          "bg-stone-50 ring-1 ring-inset ring-stone-500/20",
          "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-stone-100/80 focus-visible:ring-offset-1 focus-visible:ring-offset-stone-300",
          "text-sm font-medium placeholder:font-medium focus:font-medium focus:placeholder:font-medium",
          "tracking-wide text-stone-700",
          "focus:placeholder:normal-case focus:placeholder:tracking-wide",
          "placeholder:normal-case placeholder:italic placeholder:tracking-wide placeholder:text-stone-400",
          "focus:placeholder:text-stone-600",
          "dark:bg-slate-900",
          "dark:ring-slate-800/20",
          "dark:focus-visible:ring-slate-100/20 dark:focus-visible:ring-offset-transparent dark:focus-visible:placeholder:text-slate-400",
          "dark:focus-visible:text-white",
          "dark:focus-visible:ring-1",
        )}
      />

      <div className="absolute inset-y-0 right-0 flex space-x-2 py-2 pr-2">
        {loading && (
          <span className="inline-flex items-center px-3">
            <Loader2Icon className="h-4 w-4 animate-spin text-slate-400" strokeWidth={3} />
          </span>
        )}

        {!loading && (
          <AnimatePresence>
            <motion.span
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              className={classNames(
                "inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 text-xs font-bold text-gray-600",
                "dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300",
                {
                  "border-red-900/20 bg-red-50 text-red-600": resultsCount === 0,
                  "dark:border-transparent dark:bg-red-600/10 dark:text-red-300": resultsCount === 0,
                },
              )}
            >
              {resultsCount === 0 ? (
                noResultsText
              ) : (
                <FormattedMessage
                  defaultMessage="{resultsCount, plural, other {# resultados}}"
                  id="xjeela"
                  values={{ resultsCount }}
                />
              )}
            </motion.span>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
